package com.appsyde.shared.app.presentation

import com.appsyde.shared.core.presentation.BaseViewModel
import com.appsyde.shared.modules.auth.domain.model.DomainResult
import com.appsyde.shared.modules.auth.domain.usecase.CheckUserSessionUseCase
import com.appsyde.shared.modules.auth.domain.usecase.GetLoginUrlLandingUseCase
import com.appsyde.shared.modules.auth.domain.usecase.GetLoginUrlUseCase
import com.appsyde.shared.modules.auth.domain.usecase.GetSignupUrlUseCase
import com.appsyde.shared.modules.auth.domain.usecase.LogoutUseCase
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class SessionViewModel(
    private val getLoginUrlUseCase: GetLoginUrlUseCase,
    private val getSignupUrlUseCase: GetSignupUrlUseCase,
    private val getLoginUrlLandingUseCase: GetLoginUrlLandingUseCase,
    private val checkUserSessionUseCase: CheckUserSessionUseCase,
    private val logoutUseCase: LogoutUseCase,
    private val dispatcher: CoroutineDispatcher = Dispatchers.Default
) : BaseViewModel() {

    sealed interface SessionState {
        /** GET /auth/status in progress or about to start; show minimal "Vérification de la session" only. */
        data object CheckingSession : SessionState
        data object LoggedOut : SessionState
        data object LoggedIn : SessionState

        /**
         * Placeholder for later when /auth/status (or another endpoint) can tell us:
         * - connected = true but scopes missing
         */
        data object NeedsConsent : SessionState
    }

    private val _sessionState = MutableStateFlow<SessionState>(SessionState.CheckingSession)
    val sessionState: StateFlow<SessionState> = _sessionState.asStateFlow()

    /**
     * One-shot event consumed by UI (WebApp opens popup then calls consumeLoginUrl()).
     */
    private val _loginUrl = MutableStateFlow<String?>(null)
    val loginUrl: StateFlow<String?> = _loginUrl.asStateFlow()

    init {
        refreshSession()
    }

    fun refreshSession() {
        viewModelScope.launch(dispatcher) {
            setState(SessionState.CheckingSession)

            when (val res = checkUserSessionUseCase()) {
                is DomainResult.Success -> {
                    setState(if (res.value) SessionState.LoggedIn else SessionState.LoggedOut)
                }
                is DomainResult.Failure -> {
                    // Phase 1: on failure, assume logged out (no hard crash)
                    log("SessionViewModel.refreshSession: check failed: ${res.error}")
                    setState(SessionState.LoggedOut)
                }
            }
        }
    }

    /** V1 Landing: Créer un compte — OAuth Gmail+identity. redirectUri = window.location.origin. */
    fun startSignup(redirectUri: String) {
        viewModelScope.launch(dispatcher) {
            when (val res = getSignupUrlUseCase(redirectUri)) {
                is DomainResult.Success -> {
                    val url = res.value
                    if (url.isNotBlank()) _loginUrl.value = url
                    else log("SessionViewModel.startSignup: empty url")
                }
                is DomainResult.Failure -> {
                    log("SessionViewModel.startSignup: failed: ${res.error}")
                }
            }
        }
    }

    /** V1 Landing: Se connecter — OAuth identity-only. redirectUri = window.location.origin. */
    fun startLogin(redirectUri: String) {
        viewModelScope.launch(dispatcher) {
            when (val res = getLoginUrlLandingUseCase(redirectUri)) {
                is DomainResult.Success -> {
                    val url = res.value
                    if (url.isNotBlank()) _loginUrl.value = url
                    else log("SessionViewModel.startLogin: empty url")
                }
                is DomainResult.Failure -> {
                    log("SessionViewModel.startLogin: failed: ${res.error}")
                }
            }
        }
    }

    /** Reconnexion Gmail (NeedsConsent) — même flow que signup (Gmail+identity). */
    fun startReconnectGmail(redirectUri: String) {
        viewModelScope.launch(dispatcher) {
            when (val res = getSignupUrlUseCase(redirectUri)) {
                is DomainResult.Success -> {
                    val url = res.value
                    if (url.isNotBlank()) _loginUrl.value = url
                    else log("SessionViewModel.startReconnectGmail: empty url")
                }
                is DomainResult.Failure -> {
                    log("SessionViewModel.startReconnectGmail: failed: ${res.error}")
                }
            }
        }
    }

    fun consumeLoginUrl() {
        _loginUrl.value = null
    }

    fun logout() {
        viewModelScope.launch(dispatcher) {
            when (val res = logoutUseCase()) {
                is DomainResult.Success -> {
                    if (res.value) {
                        _loginUrl.value = null
                        setState(SessionState.LoggedOut)
                    } else {
                        log("SessionViewModel.logout: server returned false")
                    }
                }
                is DomainResult.Failure -> {
                    log("SessionViewModel.logout: failed: ${res.error}")
                }
            }
        }
    }

    fun requireConsent() {
         _sessionState.value = SessionState.NeedsConsent
    }
    private fun setState(state: SessionState) {
        _sessionState.value = state
        log("SessionState -> $state")
    }

    private fun log(msg: String) {
        println(msg)
    }
}
